<template>
  <b-row
    v-if="loaded"
    align-h="center"
  >
    <b-col
      lg="12"
      xl="9"
      class="p-2"
    >
      <div class="item-text d-flex px-2 pb-2">
        <div class="d-flex align-items-center justify-content-center">
          <i
            :class="item.icon + ' mr-3 main'"
            aria-hidden="true"
          />
        </div>
        <div class="d-flex flex-column justify-content-start">
          <small>{{ $t('dashboard.widget.pendingCases') }}</small>
          <span>{{ $t(item.label) }}</span>
        </div>
      </div>
      <div class="d-flex mt-1">
        <div
          id="pop-case-types-laboratories"
          class="form-icon d-flex mx-2 is-relative"
        >
          <i
            class="fal fa-hospital mt-2"
            aria-hidden="true"
          />
          <div class="bubble">
            <vtBubble
              :number="caseTypeChartLaboratoriesSelected.length"
              :info="true"
            />
          </div>
        </div>
        <b-popover
          v-if="caseTypeChartLaboratoriesSelected.length"
          target="pop-case-types-laboratories"
          placement="right"
          triggers="hover"
        >
          <div class="px-3 py-2">
            <div class="mb-1">
              <span><strong>{{ $t('dashboard.widget.selectedCenters') }}</strong></span>
            </div>
            <div
              v-for="(lab, index) in caseTypeChartLaboratoriesSelected"
              :key="index"
            >
              {{ lab.labName }}
            </div>
          </div>
        </b-popover>
        <vt-combo-select
          v-model="caseTypeChartLaboratoriesSelected"
          :placeholder="$t('dashboard.widget.laboratory')"
          track-by="labId"
          :options="laboratories"
          label="labName"
          :multiple="true"
          :close-on-select="false"
          :show-selection-number="true"
          @input="refreshCaseTypeChartSelected($event)"
          @select="saveConfiguration"
        />
      </div>
      <div class="d-flex mt-1">
        <div
          id="pop-case-types-priorities"
          class="form-icon d-flex mx-2 is-relative"
        >
          <i
            class="fal fa-flag mt-2"
            aria-hidden="true"
          />
          <div class="bubble">
            <vtBubble
              :number="caseTypeChartPrioritiesSelected.length"
              :info="true"
            />
          </div>
        </div>
        <b-popover
          v-if="caseTypeChartPrioritiesSelected.length"
          target="pop-case-types-priorities"
          placement="right"
          triggers="hover"
        >
          <div class="px-3 py-2">
            <div class="mb-1">
              <span><strong>{{ $t('dashboard.widget.selectedPriorities') }}</strong></span>
            </div>
            <div
              v-for="(lab, index) in caseTypeChartPrioritiesSelected"
              :key="index"
            >
              {{ lab.name }}
            </div>
          </div>
        </b-popover>
        <vt-combo-select
          v-model="caseTypeChartPrioritiesSelected"
          :placeholder="$t('dashboard.widget.priority')"
          track-by="id"
          :options="priorities"
          label="name"
          :multiple="true"
          :show-selection-number="true"
          :close-on-select="false"
          @input="refreshCaseTypeChartSelected($event)"
          @select="saveConfiguration"
        />
      </div>
      <div class="d-flex mt-1">
        <div class="form-icon d-flex mx-2 is-relative">
          <i
            class="fal fa-tags mt-2"
            aria-hidden="true"
          />
        </div>
        <vt-combo-select
          v-model="caseTypeChartSituationsSelected"
          class="mt-1"
          :placeholder="$t('dashboard.widget.caseSituation')"
          track-by="key"
          :options="caseSituations"
          :custom-label="v => [$t(v.label)].filter(Boolean).join(' ')"
          :multiple="false"
          @input="refreshCaseTypeChartSelected($event)"
          @select="saveConfiguration"
        />
      </div>
      <div class="mt-3">
        <case-type-chart
          v-if="item.subType === 'caseType'"
          ref="caseTypeChart"
          :laboratories-id="caseTypeChartLaboratoriesSelected.length ? caseTypeChartLaboratoriesSelected : laboratories"
          :priorities-id="caseTypeChartPrioritiesSelected.length ? caseTypeChartPrioritiesSelected : priorities"
          :case-situation="caseTypeChartSituationsSelected ? caseTypeChartSituationsSelected : null"
        />
      </div>
    </b-col>
  </b-row>
</template>

<script>
import VtBubble from '@/components/common/VtBubble'
import CaseTypeChart from '@/modules/dashboardModule/CaseTypeChart'
import { DASHBOARD_SITUATION_CASE_OPTIONS } from '@/api/resources/dashboard/dashboardService'
import sessionStore from '@/session/sessionStore'
import customConfigResource from '@/api/resources/ui/CustomConfigResource'
import { StoreUtils } from '@/shared/util/store-utils'

export default {
    name: 'ChartCaseType',
    components: {
        VtBubble,
        CaseTypeChart
    },
    props: {
        item: { type: Object }
    },
    data() {
        return {
            caseTypeChartLaboratoriesSelected: [],
            caseTypeChartPrioritiesSelected: [],
            caseTypeChartSituationsSelected: null,
            uiComponent: 'uicomponent.dashboard.diagnosis.casetype',
            loaded: false
        }
    },
    computed: {
        caseSituations() {
            return Object.keys(DASHBOARD_SITUATION_CASE_OPTIONS).map(key => DASHBOARD_SITUATION_CASE_OPTIONS[key])
        },
        priorities() {
            return sessionStore.state.priorities
        },
        laboratories() {
            return StoreUtils.laboratories
        }
    },
    async mounted() {
        const savedConfiguration = await customConfigResource.get(this.uiComponent)
        if (savedConfiguration && savedConfiguration.config) {
            this.caseTypeChartLaboratoriesSelected = savedConfiguration.config.caseTypeChartLaboratoriesSelected
            this.caseTypeChartPrioritiesSelected = savedConfiguration.config.caseTypeChartPrioritiesSelected
            this.caseTypeChartSituationsSelected = savedConfiguration.config.caseTypeChartSituationsSelected
        }
        this.loaded = true
    },
    methods: {
        refreshCaseTypeChartSelected() {
            this.$nextTick(() => {
                this.$refs?.caseTypeChart?.fillData()
            })
        },
        saveConfiguration() {
            this.$nextTick(() => {
                const config = {
                    caseTypeChartLaboratoriesSelected: this.caseTypeChartLaboratoriesSelected,
                    caseTypeChartPrioritiesSelected: this.caseTypeChartPrioritiesSelected,
                    caseTypeChartSituationsSelected: this.caseTypeChartSituationsSelected
                }
                customConfigResource.save(this.uiComponent, config)
            })
        }
    }
}
</script>

<style scoped>

</style>
